import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { areComponentsEqual } from 'react-hot-loader';

import { Collapse } from 'wanderu-animations';
import {
    CaretIcon,
    Checkbox,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';
import { generateIsomorphicID } from 'wanderu-utils';

import styles from './ToggleContainer.scss';

function ToggleChild(props) {
    const {
        checkboxClassName,
        children,
        className,
        isChecked,
        onChange,
        show,
    } = props;

    if (!show) {
        return null;
    }

    return (
        <li className={className}>
            <Checkbox
                checked={isChecked}
                className={checkboxClassName}
                onChange={onChange}
                delayChange
            />
            {children}
        </li>
    );
}

ToggleChild.propTypes = {
    checkboxClassName: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    isChecked: PropTypes.bool,
    onChange: PropTypes.func,
    show: PropTypes.bool,
};

class ToggleContainer extends React.Component {
    static propTypes = {
        checkboxClassName: PropTypes.string,
        children: PropTypes.node,
        className: PropTypes.string,
        disabled: PropTypes.bool,
        indeterminate: PropTypes.bool,
        innerClassName: PropTypes.string,
        isChecked: PropTypes.bool,
        isOpen: PropTypes.bool,
        moreClassName: PropTypes.string,
        numToShow: PropTypes.number,
        onChange: PropTypes.func,
        onToggleRow: PropTypes.func,
        rowClassName: PropTypes.string,
        t: PropTypes.func,
        title: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.element,
        ]),
    };

    static defaultProps = {
        checkboxClassName: '',
        className: '',
        disabled: false,
        indeterminate: false,
        innerClassName: '',
        isChecked: true,
        isOpen: false,
        moreClassName: '',
        numToShow: 6,
        onChange: () => undefined,
        rowClassName: '',
        title: '',
    };

    constructor(props) {
        super(props);

        this.state = {
            isOpen: props.isOpen,
            showMore: false,
        };
    }

    static getDerivedStateFromProps(props, state) {
        const {
            indeterminate,
        } = props;

        if (!indeterminate) {
            return null;
        }

        return {
            ...state,
            isOpen: indeterminate,
        };
    }

    countChildren() {
        let count = 0;

        React.Children.forEach(this.props.children, (child) => {
            if (!child) {
                return;
            }

            if (areComponentsEqual(child.type, ToggleChild)) {
                count += 1;
            }
        });

        return count;
    }

    showMore = () => {
        this.setState((currentState) => ({
            showMore: !currentState.showMore,
        }));
    };

    toggleRow = () => {
        if (this.props.disabled) {
            return;
        }

        if (this?.props?.onToggleRow) {
            this.props.onToggleRow(this.state.isOpen);
        }

        this.setState((currentState) => ({
            isOpen: !currentState.isOpen,
        }));
    };

    renderAugmentedChildren() {
        const {
            children,
            numToShow,
        } = this.props;
        const { showMore } = this.state;

        return React.Children.map(children, (child, i) => {
            if (!child) {
                return undefined;
            }

            if (!areComponentsEqual(child.type, ToggleChild)) {
                return child;
            }

            return React.cloneElement(
                child,
                { show: i < numToShow || showMore },
            );
        });
    }

    render() {
        const {
            isOpen,
            showMore,
        } = this.state;
        const {
            checkboxClassName,
            className,
            disabled,
            indeterminate,
            innerClassName,
            isChecked,
            moreClassName,
            numToShow,
            onChange,
            rowClassName,
            t,
            title,
        } = this.props;

        const numOfChildren = this.countChildren();
        const containerId = generateIsomorphicID({
            isChecked,
            numOfChildren,
            seed: 'container',
        });

        return (
            <div className={classNames(
                className,
                styles.container,
            )}>
                <Checkbox
                    checked={isChecked}
                    className={checkboxClassName}
                    id={containerId}
                    indeterminate={indeterminate}
                    onChange={onChange}
                    delayChange
                />

                <span className={rowClassName}>
                    <span
                        className={styles.toggleRow}
                        onClick={this.toggleRow}
                    >
                        {title}

                        {!disabled && <CaretIcon showUpArrow={isOpen} />}
                    </span>

                    <Collapse
                        duration={500}
                        isOpen={isOpen}
                    >
                        <ul className={classNames(
                            innerClassName,
                            styles.innerContainer,
                        )}>
                            {this.renderAugmentedChildren()}
                            {numOfChildren > numToShow && (
                                <li>
                                    <span
                                        className={classNames(
                                            moreClassName,
                                            styles.more,
                                        )}
                                        onClick={this.showMore}
                                    >
                                        {showMore
                                            ? t('less')
                                            : t('more')}
                                    </span>
                                </li>
                            )}
                        </ul>
                    </Collapse>
                </span>
            </div>
        );
    }
}

export {
    ToggleChild,
    ToggleContainer,
};

export default localize(ToggleContainer, ['t'], 'baseApp');
